import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import axios from 'axios';

import {
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Typography,
    CircularProgress,
    Divider,
    Box,
    Chip
} from '@mui/material';
import {
    AccountCircle,
    SwitchAccount,
    Refresh
} from '@mui/icons-material';

import { setCurrentUser } from '../../store/actions/authAction';
import setAuthToken from '../../utils/setAuthToken';
import FullScreenLoader from './FullScreenLoader';
import { markSessionSwitchStart } from './sessionSwitchUtils';

const ROLE_LABELS = {
    [-1]: 'EMPLOYÉ',
    1: 'USER',
    10: 'USER +',
    2: 'ADMIN',
    4: 'ADMIN +',
    3: 'SUPER ADMIN'
};

const STRING_ROLE_LABELS = {
    'USER': 'USER',
    'USER +': 'USER +',
    'USER+': 'USER +',
    'ADMIN': 'ADMIN',
    'ADMIN +': 'ADMIN +',
    'ADMIN+': 'ADMIN +',
    'EMPLOYÉ': 'EMPLOYÉ',
    'EMPLOYE': 'EMPLOYÉ',
    'EMPLOYEE': 'EMPLOYÉ',
    'SUPER ADMIN': 'SUPER ADMIN',
    'SUPERADMIN': 'SUPER ADMIN',
    'SUPER_ADMIN': 'SUPER ADMIN'
};

const formatRoleLabel = (roleValue) => {
    if (roleValue === null || roleValue === undefined || roleValue === '') {
        return 'Utilisateur';
    }

    const numericRole = Number(roleValue);
    if (!Number.isNaN(numericRole) && ROLE_LABELS[numericRole]) {
        return ROLE_LABELS[numericRole];
    }

    const roleString = String(roleValue).toUpperCase();
    if (STRING_ROLE_LABELS[roleString]) {
        return STRING_ROLE_LABELS[roleString];
    }

    return roleString;
};

const AccountSwitcher = ({ auth, setCurrentUser }) => {
    const dispatch = useDispatch();
    const [anchorEl, setAnchorEl] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [switching, setSwitching] = useState(false);
    const [isMounted, setIsMounted] = useState(true);
    const [organizationNames, setOrganizationNames] = useState({});
    const switchingStartRef = useRef(null);

    const isMenuOpen = Boolean(anchorEl);

    // Cleanup pour éviter les warnings React
    useEffect(() => {
        return () => {
            setIsMounted(false);
        };
    }, []);

    // Récupérer le nom de l'organisation pour un utilisateur
    const fetchOrganizationName = async (userId) => {
        try {
            const response = await axios.get(`/organization-accounts/user/${userId}/organization-name`);
            if (response.data && response.data.organization_name) {
                return { userId, organizationName: response.data.organization_name };
            }
            return { userId, organizationName: null };
        } catch (error) {
            console.error(`Erreur lors de la récupération du nom d'organisation pour l'utilisateur ${userId}:`, error);
            return { userId, organizationName: null };
        }
    };

    // Récupérer les noms d'organisation pour tous les comptes
    const fetchOrganizationNames = useCallback(async (accountsList) => {
        if (!accountsList || accountsList.length === 0) {
            return;
        }

        try {
            // Récupérer tous les noms d'organisation en parallèle
            const promises = accountsList.map(account => 
                fetchOrganizationName(account.user_id)
            );
            const results = await Promise.all(promises);
            
            // Créer un objet avec les noms d'organisation par user_id
            const namesMap = {};
            results.forEach(({ userId, organizationName }) => {
                if (organizationName) {
                    namesMap[userId] = organizationName;
                }
            });
            
            if (isMounted) {
                setOrganizationNames(namesMap);
            }
        } catch (error) {
            console.error('Erreur lors de la récupération des noms d\'organisation:', error);
        }
    }, [isMounted]);

    // Récupérer la liste des comptes utilisateur
    const fetchUserAccounts = useCallback(async () => {
        try {
            setLoading(true);
            console.log('test-userIdtarget: Récupération de la liste des comptes...');
            
            const response = await axios.get('/users/user-accompt-list');
            
            console.log('test-userIdtarget: Réponse de la liste des comptes:', response);
            console.log('test-userIdtarget: Status:', response.status);
            console.log('test-userIdtarget: Données:', response.data);
            
            if (response.data && response.data.data) {
                console.log('test-userIdtarget: Structure des comptes:', response.data.data);
                console.log('test-userIdtarget: Premier compte:', response.data.data[0]);
                if (isMounted) {
                    setAccounts(response.data.data);
                    // Récupérer les noms d'organisation pour tous les comptes
                    fetchOrganizationNames(response.data.data);
                }
                console.log('test-userIdtarget: Comptes chargés:', response.data.data);
            } else {
                console.log('test-userIdtarget: Aucune donnée dans la réponse');
            }
        } catch (error) {
            console.error('test-userIdtarget: Erreur lors de la récupération des comptes:', error);
            console.error('test-userIdtarget: Détails de l\'erreur:', error.response);
            console.error('test-userIdtarget: Status de l\'erreur:', error.response?.status);
            console.error('test-userIdtarget: Données de l\'erreur:', error.response?.data);
            // Optionnel: afficher une notification d'erreur
        } finally {
            if (isMounted) {
                setLoading(false);
            }
        }
    }, [fetchOrganizationNames, isMounted]);

    // Ouvrir le menu et charger les comptes
    const handleMenuOpen = (event) => {
        console.log('test-userIdtarget: Ouverture du menu de basculement de compte');
        setAnchorEl(event.currentTarget);
    };

    // Fermer le menu
    const handleMenuClose = () => {
        if (isMounted) {
            setAnchorEl(null);
        }
    };

    // Basculer vers un autre compte
    const handleAccountSwitch = async (targetUserId) => {
        try {
            setSwitching(true);
            switchingStartRef.current = Date.now();
            console.log('test-userIdtarget: Début du basculement vers l\'utilisateur:', targetUserId);
            
            const response = await axios.post(`/users/login/${targetUserId}`);
            
            console.log('test-userIdtarget: Réponse complète de l\'API:', response);
            console.log('test-userIdtarget: Status de la réponse:', response.status);
            console.log('test-userIdtarget: Données de la réponse:', response.data);
            
            if (response.data && response.data.token && response.data.user) {
                const token = response.data.token;
                const user = response.data.user;
                
                console.log('test-userIdtarget: Token reçu:', token);
                console.log('test-userIdtarget: User reçu:', user);
                
                // Sauvegarder le nouveau token
                localStorage.setItem("jwtToken_compta", token);
                setAuthToken(token, user.user_id, "USER");
                
                // Déclencher un événement personnalisé pour notifier les iframes du changement de token
                const tokenChangeEvent = new CustomEvent('tokenChanged', {
                    detail: { token: token, user: user }
                });
                window.dispatchEvent(tokenChangeEvent);
                
                console.log('test-userIdtarget: Token sauvegardé dans localStorage et configuré dans axios');
                console.log('test-userIdtarget: Headers axios après setAuthToken:', axios.defaults.headers.common);
                console.log('test-userIdtarget: Événement tokenChanged déclenché pour les iframes');
                
                // Créer l'objet utilisateur avec la structure attendue par Redux
                // Utiliser directement les données du backend sans décoder le token
                const currentUser = {
                    id: user.user_id,
                    username: user.email,
                    email: user.email,
                    role: user.user_role, // Utiliser directement user_role du backend
                    user_employee_id: user.user_employee_id,
                    user_type: user.user_type,
                    USER_TYPE: "USER",
                    // Ajouter toutes les propriétés de l'utilisateur du backend
                    ...user
                };
                
                console.log('test-userIdtarget: Utilisateur mis à jour:', currentUser);
                console.log('test-userIdtarget: Role final:', currentUser.role);
                console.log('test-userIdtarget: User_role du backend:', user.user_role);
                
                // Mettre à jour directement le store Redux sans passer par setCurrentUser
                // pour éviter l'appel automatique à getUserInfo() qui pourrait échouer
                dispatch({
                    type: "SET_CURRENT_USER",
                    payload: currentUser
                });
                
                // Mettre à jour les informations utilisateur dans le store
                dispatch({
                    type: "GET_USER_INFO",
                    payload: user
                });
                
                console.log('test-userIdtarget: Store Redux mis à jour directement');
                
                // Fermer le menu
                handleMenuClose();
                
                console.log('test-userIdtarget: Basculement terminé, rechargement complet de la page pour avoir les bonnes données');
                
                // Marquer le changement de session AVANT le rechargement pour que l'overlay s'affiche immédiatement
                markSessionSwitchStart();
                
                // Forcer le rechargement complet de la page pour vider tous les caches
                // Cela garantit que l'iframe reçoit les bonnes données du nouveau compte
                setTimeout(() => {
                    console.log('test-userIdtarget: Rechargement de la page dans 100ms...');
                    window.location.reload();
                }, 100); // Petit délai pour permettre la sauvegarde du token
            } else {
                console.error('test-userIdtarget: Réponse API invalide:', response.data);
            }
        } catch (error) {
            console.error('test-userIdtarget: Erreur lors du basculement de compte:', error);
            console.error('test-userIdtarget: Détails de l\'erreur:', error.response);
            console.error('test-userIdtarget: Status de l\'erreur:', error.response?.status);
            console.error('test-userIdtarget: Données de l\'erreur:', error.response?.data);
            // Optionnel: afficher une notification d'erreur
        } finally {
            const finishSwitching = () => {
                if (isMounted) {
                    setSwitching(false);
                }
                switchingStartRef.current = null;
            };
            const startedAt = switchingStartRef.current;
            const elapsed = startedAt ? Date.now() - startedAt : 0;
            const remaining = Math.max(0, 3000 - elapsed);
            if (remaining > 0) {
                setTimeout(finishSwitching, remaining);
            } else {
                finishSwitching();
            }
        }
    };

    const currentUserId = auth?.user?.user_id || auth?.user?.id;
    const currentUserOrganizationName = currentUserId ? organizationNames[currentUserId] : null;
    const currentUserFirstName = auth?.user?.first_name || auth?.user?.firstname || auth?.user?.user_first_name || '';
    const currentUserLastName = auth?.user?.last_name || auth?.user?.lastname || auth?.user?.user_last_name || '';
    const currentUserFullName = `${currentUserFirstName} ${currentUserLastName}`.trim() || auth?.user?.email || '';

    useEffect(() => {
        if (currentUserId) {
            fetchUserAccounts();
        }
    }, [currentUserId, fetchUserAccounts]);

    const handleRefreshAccounts = () => {
        fetchUserAccounts();
    };


/*
    // Log de l'état d'authentification au moment du rendu
    console.log('test-userIdtarget: Rendu du composant - auth.user:', auth.user);
    console.log('test-userIdtarget: Rendu du composant - auth.user.user_super_email:', auth.user?.user_super_email);
    console.log('test-userIdtarget: Rendu du composant - auth.user.super_email:', auth.user?.super_email);
    console.log('test-userIdtarget: Rendu du composant - auth.user.user_superEmail:', auth.user?.user_superEmail);
    console.log('test-userIdtarget: Rendu du composant - auth.user.superEmail:', auth.user?.superEmail);
    console.log('test-userIdtarget: Rendu du composant - Toutes les propriétés de auth.user:', Object.keys(auth.user || {}));
    console.log('test-userIdtarget: Rendu du composant - accounts.length:', accounts.length);
    console.log('test-userIdtarget: Rendu du composant - currentUserAccount:', currentUserAccount);
*/
    return (
        <>
            <IconButton
                aria-controls={isMenuOpen ? 'account-switcher-menu' : undefined}
                aria-expanded={isMenuOpen}
                aria-haspopup="true"
                onClick={handleMenuOpen}
                color="inherit"
                size="large"
                title="Basculer entre les comptes"
            >
                <SwitchAccount />
            </IconButton>

            <FullScreenLoader
                open={switching}
                message="Changement de compte..."
            />

            <Menu
                id="account-switcher-menu"
                anchorEl={anchorEl}
                open={isMenuOpen}
                onClose={handleMenuClose}
                PaperProps={{
                    style: {
                        maxHeight: 400,
                        width: 300,
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <Box sx={{ p: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography variant="h6" component="div" sx={{ mb: 0.5 }}>
                            Mes comptes 
                        </Typography>
                        {currentUserFullName && (
                            <Typography variant="body2" sx={{ color: "black", fontWeight: 'medium' }}>
                                {currentUserFullName}
                            </Typography>
                        )}
                    </Box>
                    <IconButton
                        size="small"
                        onClick={handleRefreshAccounts}
                        disabled={loading}
                        title="Recharger la liste"
                    >
                        {loading ? (
                            <CircularProgress size={18} />
                        ) : (
                            <Refresh fontSize="small" />
                        )}
                    </IconButton>
                </Box>
                
                <Divider />
                
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <>
                        {accounts.map((account) => {
                            // Essayer différentes propriétés pour les noms
                            const firstName = account.first_name || account.firstname || account.user_first_name || '';
                            const lastName = account.last_name || account.lastname || account.user_last_name || '';
                            const roleRaw = account.user_role || account.role || account.user_type || account.USER_TYPE || null;
                            const role = formatRoleLabel(roleRaw);
                            
                            // Vérifier si c'est le compte actuel avec plusieurs critères
                            const isCurrentAccount = account.user_id === auth.user?.user_id || 
                                                account.user_id === auth.user?.id 
                                            
                            
                            // Récupérer le nom de l'organisation pour ce compte
                            const organizationName = organizationNames[account.user_id];
                       
                            const displayName = organizationName || '...'
                            
                            console.log('test-userIdtarget: Account pour affichage:', account);
                            console.log('test-userIdtarget: firstName:', firstName, 'lastName:', lastName);
                            console.log('test-userIdtarget: isCurrentAccount:', isCurrentAccount);
                            
                            return (
                                <MenuItem
                                    key={account.user_id}
                                    onClick={() => handleAccountSwitch(account.user_id)}
                                    disabled={switching || isCurrentAccount}
                                    selected={isCurrentAccount}
                                >
                                    <ListItemIcon>
                                        <AccountCircle 
                                            color={isCurrentAccount ? 'primary' : 'inherit'} 
                                        />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={displayName}
                                        secondary={
                                            <Box>
                                                {organizationName ? (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {" "}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {""}
                                                    </Typography>
                                                )}
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {role}
                                                    </Typography>
                                                    {isCurrentAccount && (
                                                        <Chip
                                                            label="Compte actuel"
                                                            color="primary"
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Box>
                                            </Box>
                                        }
                                    />
                                </MenuItem>
                            );
                        })}
                        
                        {accounts.length === 0 && !loading && (
                            <MenuItem disabled>
                                <ListItemText primary="Aucun compte trouvé" />
                            </MenuItem>
                        )}
                    </>
                )}
                
            </Menu>
        </>
    );
};

const mapStateToProps = (state) => ({
    auth: state.auth,
});

const mapDispatchToProps = (dispatch) => ({
    setCurrentUser: bindActionCreators(setCurrentUser, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(AccountSwitcher);
